/**
 * Vorlage „Verquirlen“.
 *
 * Aufgeschlagene Eier in einer Schüssel: Zwei Dotter liegen im Eiklar. Gabel
 * oder Schneebesen schlagen schnell hin und her, die Dotter verlaufen, bis
 * eine gleichmäßige, leicht schaumige Masse entsteht.
 */
import { clamp, ease, fract, segment, wave } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { circle, compact, ellipse, faded, group } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { motionLines } from '../parts/effects.ts';
import { counter, doubleArrow } from '../parts/stage.ts';
import { fork, place, whisk } from '../parts/utensils.ts';
import { bowl } from '../parts/vessels.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

const BOWL = { cx: 112, rim: 80, width: 140, height: 54 } as const;
const LEVEL = 0.2;

/** Zeitplan. */
const TIME = { beat: [0.06, 0.78], reset: [0.9, 1] } as const;

/** Vorlage „Verquirlen“. */
export const mixBeatTemplate = defineTemplate({
  id: 'mix.beat',
  group: 'mix',
  title: 'Verquirlen',
  description:
    'Eier werden mit der Gabel oder dem Schneebesen kräftig geschlagen, bis Eigelb und Eiweiß eine gleichmäßige Masse ergeben.',
  durationSeconds: 5,
  keywords: ['verquirlen', 'verschlagen', 'verklopfen', 'eier verquirlen', 'eier verschlagen', 'verkleppern'],
  stillTime: 0.4,
  parameters: [choice('tool', 'Werkzeug', { fork: 'Gabel', whisk: 'Schneebesen' }, 'fork')],
  inferValues(text) {
    const lower = text.toLowerCase();
    if (lower.includes('schneebesen')) return { tool: 'whisk' };
    if (lower.includes('gabel')) return { tool: 'fork' };
    return {};
  },
  scene(values, t) {
    const tool = textValue(values, 'tool', 'fork');
    const vessel = bowl(BOWL.cx, BOWL.rim, BOWL.width, BOWL.height);
    const surfaceY = BOWL.rim + vessel.depth * LEVEL;
    const rx = vessel.widthAt(LEVEL);
    const ry = vessel.ry * (rx / vessel.rx);
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const beaten = ease.inOutSine(segment(t, ...TIME.beat));
    const active = segment(t, 0.03, 0.08) * (1 - segment(t, 0.8, 0.86));

    // Eiklar mit zwei Dottern, die beim Schlagen verlaufen.
    const yolk = (dx: number, dy: number, amount: number): SceneNode | null => {
      const smear = amount * 1.4;
      if (smear >= 1) {
        return null;
      }
      return ellipse(BOWL.cx + dx, surfaceY + dy, 10 * (1 + smear * 1.6), 6 * (1 - smear * 0.5), {
        fill: 'shade',
        stroke: 'ink',
        strokeWidth: STROKE.detail,
        opacity: clamp(1.2 - smear),
      });
    };
    const surface = (amount: number): SceneNode =>
      group(
        [
          ellipse(BOWL.cx, surfaceY, rx, ry, { fill: 'tint', stroke: 'ink', strokeWidth: STROKE.detail }),
          yolk(-16, 1, amount),
          yolk(14, -2, amount),
          faded(clamp(amount * 1.2 - 0.3), [
            ellipse(BOWL.cx, surfaceY, rx, ry, { fill: 'shade', stroke: 'ink', strokeWidth: STROKE.detail }),
            // Feiner Schaum
            ...[-30, -12, 6, 22, 34, -22, 14].map((dx, index) =>
              circle(BOWL.cx + dx, surfaceY + ((index % 3) - 1) * 4, 1.6 + (index % 2), {
                fill: 'paper',
                stroke: 'deep',
                strokeWidth: STROKE.fine,
              }),
            ),
          ]),
        ],
        { clip: vessel.opening },
      );

    // Schnelles Hin und Her, leicht schräg.
    const swing = wave(t, 10);
    const x = BOWL.cx + swing * rx * 0.42 * active;
    const y = surfaceY + 3 - Math.abs(swing) * 2;
    const lean = 32 + swing * 12 * active;
    const toolNode =
      tool === 'whisk' ? place(x, y, lean, whisk(fract(t * 20)), 1.05) : place(x, y, lean, fork(), 1.1);
    return compact([
      ...counter(),
      ...vessel.back,
      reset > 0 ? group([surface(0)]) : null,
      faded(1 - reset, [surface(beaten)]),
      toolNode,
      ...vessel.front,
      motionLines(BOWL.cx + BOWL.width / 2 + 10, surfaceY - 6, 180, 12, 3, 6, active * 0.9),
    ]);
  },
  hints() {
    return [doubleArrow({ x: BOWL.cx - 44, y: BOWL.rim - 16 }, { x: BOWL.cx + 44, y: BOWL.rim - 16 })];
  },
});
